import { Autocomplete, CircularProgress, debounce, TextField } from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { quoteLineType, repCodeType } from './types'
import { BundleLineItem } from './EditQuote/lines/lineItemPricing'
import { useManufacturerData } from './ManufacturerList/useManufacturerData'

export type CRMSearchValue = {
  id: string
  label: string
  accountId?: string | null
  accountName?: string | null
}

export function CRMSearch(props: {
  object: 'Opportunity' | 'Account' | 'Contact'
  value: CRMSearchValue | null
  label: string
  accountId?: string
  disabled?: boolean
  error?: boolean
  errorText?: string
  onChange: (e: React.SyntheticEvent<Element, Event>, value: CRMSearchValue | null) => void
}) {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<CRMSearchValue[]>([])

  const needsAccount = props.object === 'Contact'

  useEffect(() => {
    setSearchResults(props.value ? [{ id: props.value.id, label: props.value.label }] : [])
  }, [])

  const handleSearchOpps = (value: string, accountId?: string) => {
    if (value.length < 3 || (needsAccount && !accountId)) {
      setSearchResults([])
      return
    }

    setSearchLoading(true)
    axios
      .get(`/search${props.object}`, {
        params: { search: value, ...(accountId ? { accountId } : {}) },
      })
      .then(res => {
        setSearchResults(
          res.data.map(
            (item: { id: string; name: string; accountId?: string | null; accountName?: string | null }) => ({
              id: item.id,
              label: item.name,
              ...(props.object === 'Opportunity' ? { accountId: item.accountId, accountName: item.accountName } : {}),
            }),
          ),
        )
      })
      .catch(err => {
        setSearchResults([])
        console.error(err)
      })
      .finally(() => {
        setSearchLoading(false)
      })
  }
  const debouncedHandleSearchOpps = useRef(debounce(handleSearchOpps, 500)).current

  useEffect(() => {
    if (needsAccount && !props.accountId) {
      setSearchResults([])
      return
    }
    setSearchResults(props.value ? [{ id: props.value.id, label: props.value.label }] : [])
  }, [needsAccount, props.accountId])

  return (
    <Autocomplete
      fullWidth
      disabled={props.disabled || (needsAccount && !props.accountId)}
      options={searchResults}
      filterOptions={x => x}
      inputValue={searchInputValue}
      loading={searchLoading}
      renderInput={params => (
        <TextField
          {...params}
          label={props.label}
          error={props.error}
          helperText={props.error ? props.errorText : ''}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
            },
          }}
        />
      )}
      size="small"
      value={props.value}
      onInputChange={(e, value, reason) => {
        setSearchInputValue(value)
        if (reason !== 'selectOption' && reason !== 'reset') {
          debouncedHandleSearchOpps(value, props.object === 'Account' ? undefined : props.accountId)
        }
      }}
      onChange={props.onChange}
    />
  )
}

export function ManufacturerSearchAutocomplete({
  value,
  onChange,
  label,
  className,
  fullWidth,
  autoFocus,
  onBlur,
  contractNumber,
}: {
  value: string | null
  onChange: (value: string | null, oemCode: string | null) => void
  label?: string
  className?: string
  fullWidth?: boolean
  autoFocus?: boolean
  onBlur?: () => void
  contractNumber?: string | null
}) {
  const { rows: mfrs } = useManufacturerData()
  const contract = contractNumber?.toLowerCase() ?? ''
  const contractAliases = useMemo(
    () =>
      mfrs.flatMap(mfr =>
        mfr.contractAliases
          .filter(entry => entry.contractNumber === contract)
          .map(entry => ({ alias: entry.alias, mfr })),
      ),
    [mfrs, contract],
  )
  const usesContractList = contractAliases.length > 0
  const options = useMemo(
    () =>
      usesContractList
        ? [...new Set(contractAliases.map(entry => entry.alias))].sort()
        : [...new Set(mfrs.flatMap(mfr => [mfr.supplier, ...mfr.aliases]))].sort(),
    [usesContractList, contractAliases, mfrs],
  )

  const [inputValue, setInputValue] = useState(value ?? '')
  const query = inputValue.trim()

  return (
    <Autocomplete
      className={className}
      size="small"
      fullWidth={fullWidth}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      filterOptions={(opts, state) => {
        const input = state.inputValue.trim().toLowerCase()
        if (input.length < 3) return []
        return opts.filter(opt => opt.toLowerCase().includes(input))
      }}
      onChange={(_, next) => {
        const row = !next
          ? undefined
          : usesContractList
          ? contractAliases.find(entry => entry.alias === next)?.mfr
          : mfrs.find(mfr => mfr.supplier === next || mfr.aliases.includes(next))
        onChange(next, row?.mfrCode ?? row?.mfrCodeUpper ?? null)
      }}
      onBlur={onBlur}
      noOptionsText={query.length < 3 ? 'Type at least 3 characters' : 'No matching manufacturers'}
      renderInput={params => <TextField {...params} label={label} size="small" autoFocus={autoFocus} />}
    />
  )
}

export function SupplierSearchAutocomplete({
  value,
  onChange,
  label,
  className,
  fullWidth,
  autoFocus,
  onBlur,
}: {
  value: string | null
  onChange: (value: string | null) => void
  label?: string
  className?: string
  fullWidth?: boolean
  autoFocus?: boolean
  onBlur?: () => void
}) {
  const [searchInputValue, setSearchInputValue] = useState(value ?? '')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<string[]>(value ? [value] : [])

  const handleSearch = (query: string) => {
    if (query.length < 3) {
      setSearchResults([])
      return
    }

    setSearchLoading(true)
    axios
      .get('/supplier', {
        params: { supplier: query, page: 0, pageSize: 50, status: 'approved' },
      })
      .then(res => {
        setSearchResults((res.data.list as { name: string }[]).map(supplier => supplier.name))
      })
      .catch(err => {
        setSearchResults([])
        console.error(err)
      })
      .finally(() => {
        setSearchLoading(false)
      })
  }
  const debouncedHandleSearch = useRef(debounce(handleSearch, 500)).current

  return (
    <Autocomplete
      className={className}
      size="small"
      fullWidth={fullWidth}
      options={value && !searchResults.includes(value) ? [value, ...searchResults] : searchResults}
      filterOptions={x => x}
      value={value}
      inputValue={searchInputValue}
      loading={searchLoading}
      onInputChange={(_, next, reason) => {
        setSearchInputValue(next)
        if (reason !== 'selectOption' && reason !== 'reset') {
          debouncedHandleSearch(next)
        }
      }}
      onChange={(_, next) => onChange(next ?? null)}
      onBlur={onBlur}
      noOptionsText={searchInputValue.length < 3 ? 'Type at least 3 characters' : 'No matching suppliers'}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
            },
          }}
        />
      )}
    />
  )
}

export function NetsuiteVendorSearchAutocomplete({
  value,
  onChange,
  label,
  className,
  fullWidth,
  autoFocus,
  onBlur,
}: {
  value: string | null
  onChange: (value: string | null) => void
  label?: string
  className?: string
  fullWidth?: boolean
  autoFocus?: boolean
  onBlur?: () => void
}) {
  const [searchInputValue, setSearchInputValue] = useState(value ?? '')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<string[]>(value ? [value] : [])

  useEffect(() => {
    setSearchInputValue(value ?? '')
    setSearchResults(prev => (value && !prev.includes(value) ? [value, ...prev] : prev))
  }, [value])

  const handleSearch = (query: string) => {
    if (query.length < 3) {
      setSearchResults([])
      return
    }
    setSearchLoading(true)
    axios
      .get('/netsuite/vendors', {
        params: { filter: query },
      })
      .then(res => {
        setSearchResults(res.data.map((vendor: { companyname: string }) => vendor.companyname))
      })
      .catch(err => {
        setSearchResults([])
        console.error(err)
      })
      .finally(() => {
        setSearchLoading(false)
      })
  }
  const debouncedHandleSearch = useRef(debounce(handleSearch, 500)).current

  return (
    <Autocomplete
      className={className}
      size="small"
      fullWidth={fullWidth}
      options={searchResults}
      filterOptions={x => x}
      value={value}
      inputValue={searchInputValue}
      loading={searchLoading}
      onInputChange={(_, next, reason) => {
        setSearchInputValue(next)
        if (reason !== 'selectOption' && reason !== 'reset') {
          debouncedHandleSearch(next)
        }
      }}
      onChange={(_, next) => onChange(next ?? null)}
      onBlur={onBlur}
      noOptionsText={searchInputValue.length < 3 ? 'Type at least 3 characters' : 'No matching vendors'}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
            },
          }}
        />
      )}
    />
  )
}

export const formatCurrency = (value: number | null | undefined, whenEmpty = '$0.00'): string =>
  value != null ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : whenEmpty

export const ownerFilterQueryParam = (email: string | null, role: { admin: boolean; operations: boolean }): string =>
  !role.admin && !role.operations ? `&owner=${email}` : ''

export type LineGroupAccessors<T> = {
  getNumber: (item: T) => number | null | undefined
  getGroup: (item: T) => string | null | undefined
}

export type NestedLine<T> = T & { children?: T[] }

export const quoteBundleAccessors: LineGroupAccessors<{ LineNumber: number; Bundle?: string | null }> = {
  getNumber: item => item.LineNumber,
  getGroup: item => item.Bundle,
}

export const portalKitAccessors: LineGroupAccessors<{ Number?: number | null; Kit?: string | null }> = {
  getNumber: item => item.Number,
  getGroup: item => item.Kit,
}

function hasGroup<T>(item: T, getGroup: LineGroupAccessors<T>['getGroup']): boolean {
  const group = getGroup(item)
  return group != null && group !== ''
}

// Flat rows to headers with children attached
export function nestLineItems<T>(items: T[], accessors: LineGroupAccessors<T>): NestedLine<T>[] {
  if (items.some(item => ((item as NestedLine<T>).children?.length ?? 0) > 0)) {
    return items as NestedLine<T>[]
  }

  return items
    .filter(line => accessors.getNumber(line) === -1 || !hasGroup(line, accessors.getGroup))
    .map(line => {
      if (accessors.getNumber(line) !== -1 || !hasGroup(line, accessors.getGroup)) {
        return { ...line, children: undefined }
      }
      const group = accessors.getGroup(line)
      const children = items
        .filter(
          child =>
            accessors.getGroup(child) === group &&
            accessors.getNumber(child) !== -1 &&
            hasGroup(child, accessors.getGroup),
        )
        .sort((a, b) => (accessors.getNumber(a) ?? 0) - (accessors.getNumber(b) ?? 0))
      return { ...line, children }
    })
    .sort((a, b) => {
      const sortKey = (item: NestedLine<T>) =>
        accessors.getNumber(item) === -1 && item.children?.length
          ? Math.min(...item.children.map(c => accessors.getNumber(c) ?? Infinity))
          : accessors.getNumber(item) ?? Infinity
      return sortKey(a) - sortKey(b)
    })
}

// Nested headers to flat list (header, then its children)
export function flattenLineItems<T extends { children?: T[] }>(
  items: T[],
  options?: {
    getId?: (item: T) => string | null | undefined
    isHeader?: (item: T) => boolean
  },
): T[] {
  const rootIds = options?.getId
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      new Set(items.map(item => options.getId!(item)).filter((id): id is string => id != null && id !== ''))
    : null
  const isHeader = options?.isHeader ?? ((item: T) => (item.children?.length ?? 0) > 0)

  return items.flatMap(item => {
    if (!isHeader(item)) return [item]
    const children = (item.children ?? []).filter(child => {
      if (!rootIds || !options?.getId) return true
      const id = options.getId(child)
      return id == null || id === '' || !rootIds.has(id)
    })
    return [item, ...children]
  })
}

// Ordered flat list (header immediately followed by its children) to nested
export function unflattenLineItems<T>(flat: T[], accessors: LineGroupAccessors<T>): NestedLine<T>[] {
  const result: NestedLine<T>[] = []
  let i = 0
  while (i < flat.length) {
    const item = { ...flat[i] } as NestedLine<T>
    if (accessors.getNumber(item) === -1 && hasGroup(item, accessors.getGroup)) {
      const group = accessors.getGroup(item)
      const children: T[] = []
      i++
      while (i < flat.length && accessors.getGroup(flat[i]) === group && (accessors.getNumber(flat[i]) ?? -2) !== -1) {
        children.push({ ...flat[i] })
        i++
      }
      result.push({ ...item, children })
    } else {
      result.push(item)
      i++
    }
  }
  return result
}

export const flattenBundledStructure = (bundledItems: BundleLineItem[]): quoteLineType[] =>
  flattenLineItems(bundledItems, {
    getId: item => item.LineItemId,
    isHeader: item => item.LineNumber === -1,
  })

export const renumberFlatLineItems = (flatLineItems: quoteLineType[]): quoteLineType[] => {
  let lineNumber = 1
  return flatLineItems.map(item => (item.LineNumber === -1 ? item : { ...item, LineNumber: lineNumber++ }))
}

export const getRepCodeLabel = (repCode: repCodeType | null): string => {
  if (!repCode) return ''
  return `${repCode.RepCode} ${[repCode.InsideSalesRep, repCode.AccountExecutive, repCode.Engineer]
    .filter(Boolean)
    .join(', ')}`
}

export function parseDate(value: unknown): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(String(value))
  return Number.isNaN(date.getTime()) ? null : date
}
